import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';

import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { environment } from '../environments/environment';
import { globalRoutes } from '../global.routes';
import { initializeLogging } from './initializers/logging.initializer';
import { mpsFetchBearer } from './initializers/mps-oauth2.initializer';
import { mpsFetchUser } from './initializers/mps-user.initializer';
import { mpsLoaderInterceptor } from './interceptors/mps-loader.interceptor';
import { mpsTokenInterceptor } from './interceptors/mps-token.interceptor';
import { MpsUnauthenticatedInterceptor } from './interceptors/mps-unauthenticated.interceptor';
import { LoggingService } from './services/logging.service';
import { MpsOauth2Service } from './services/mps-oauth2.service';
import { MpsUserService } from './services/mps-user.service';

export const FR_DATE_FORMAT: MatDateFormats = {
  parse: {
    dateInput: 'MM-DD-YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

function getTranslatePrefix(): string {
  if (environment.translationsSource === 'server') {
    return environment.api.host + '/lokalise/';
  }
  return './assets/i18n/';
}

export const mpsConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      // Angular packages
      BrowserModule,
      MatDatepickerModule,
      MatMomentDateModule
    ),
    provideTranslateService({
      fallbackLang: environment.defaultLocale,
      loader: provideTranslateHttpLoader({ prefix: getTranslatePrefix(), suffix: '.json' }),
    }),
    provideHttpClient(withXhr(), withInterceptors([mpsTokenInterceptor]), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    { provide: HTTP_INTERCEPTORS, useClass: mpsLoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MpsUnauthenticatedInterceptor, multi: true },
    { provide: 'OAUTH2_PROVIDERS_URL', useValue: environment.api.providers },
    { provide: 'API_BASE_URL', useValue: environment.api.host },
    { provide: 'LOADER_ON_HTTP', useValue: environment.loaderOnHttp },
    provideAppInitializer(() => {
      const initializerFn = mpsFetchBearer(inject(MpsOauth2Service), inject(MpsUserService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = mpsFetchUser(inject(MpsUserService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeLogging(inject(LoggingService));
      return initializerFn();
    }),
    MatDatepickerModule,
    MatMomentDateModule,
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: MAT_DATE_FORMATS, useValue: FR_DATE_FORMAT },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { disableClose: true, width: '50vw', minWidth: '800px' },
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', floatLabel: 'auto' },
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 250,
        position: 'above',
        disableTooltipInteractivity: true,
      },
    },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideRouter(
      globalRoutes,
      // withDebugTracing(), // Uncomment the following line to enable routing debug trace
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      })
    ),
  ],
};
